import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Trash2, Upload } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleTRAINING } from "@/components/training/ModuleTitleTRAINING";

export default function TrainingContentEdit() {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [meta, setMeta] = useState({});
  const [quiz, setQuiz] = useState({ enabled: false, passing_score_pct: 80, questions: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/training/trainings/${trainingId}`);
      setTraining(data);
      setMeta({
        title: data.title,
        description: data.description || "",
        category: data.category || "",
        duration_minutes: data.duration_minutes || 0,
        required: !!data.required,
        due_date: data.due_date || "",
        video_source: data.video_source,
        video_embed_url: data.video_embed_url || "",
        visible_to_volunteers: !!data.visible_to_volunteers,
      });
      setQuiz({
        enabled: !!data.quiz?.enabled,
        passing_score_pct: data.quiz?.passing_score_pct || 80,
        questions: (data.quiz?.questions || []).map((q) => ({
          id: q.id,
          text: q.text,
          choices: (q.choices || []).map((c) => ({ text: c.text, correct: !!c.correct })),
        })),
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      navigate("/training/admin");
    }
  };

  useEffect(() => {
    load();
     
  }, [trainingId]);

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.put(`/training/trainings/${trainingId}`, {
        ...meta,
        due_date: meta.due_date || null,
      });
      toast.success("Details saved.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveQuiz = async () => {
    setBusy(true);
    try {
      await api.put(`/training/trainings/${trainingId}/quiz`, {
        enabled: quiz.enabled,
        passing_score_pct: quiz.passing_score_pct,
        questions: quiz.questions.map((q, i) => ({
          id: q.id || `q${i + 1}`,
          text: q.text,
          choices: q.choices,
        })),
      });
      toast.success("Quiz saved.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/training/trainings/${trainingId}/video`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded (${Math.round(data.size_bytes / 1024)} KB).`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const addQuestion = () => {
    setQuiz({
      ...quiz,
      enabled: true,
      questions: [
        ...quiz.questions,
        {
          id: `q${quiz.questions.length + 1}`,
          text: "",
          choices: [{ text: "", correct: false }, { text: "", correct: false }],
        },
      ],
    });
  };
  const removeQuestion = (i) => {
    const qs = [...quiz.questions];
    qs.splice(i, 1);
    setQuiz({ ...quiz, questions: qs });
  };
  const updateQuestion = (i, patch) => {
    const qs = [...quiz.questions];
    qs[i] = { ...qs[i], ...patch };
    setQuiz({ ...quiz, questions: qs });
  };
  const updateChoice = (qi, ci, patch) => {
    const qs = [...quiz.questions];
    const choices = [...qs[qi].choices];
    if (patch.correct) {
      // Only one correct per question.
      choices.forEach((c, i) => (choices[i] = { ...c, correct: i === ci }));
    } else if (patch.text !== undefined) {
      choices[ci] = { ...choices[ci], text: patch.text };
    }
    qs[qi] = { ...qs[qi], choices };
    setQuiz({ ...quiz, questions: qs });
  };
  const addChoice = (qi) => {
    const qs = [...quiz.questions];
    qs[qi] = {
      ...qs[qi],
      choices: [...qs[qi].choices, { text: "", correct: false }],
    };
    setQuiz({ ...quiz, questions: qs });
  };
  const removeChoice = (qi, ci) => {
    const qs = [...quiz.questions];
    const choices = [...qs[qi].choices];
    choices.splice(ci, 1);
    qs[qi] = { ...qs[qi], choices };
    setQuiz({ ...quiz, questions: qs });
  };

  if (!training) {
    return (
      <div className="patria-card p-8">
        <p className="text-charcoal-muted italic">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="training-edit-page">
      <Link
        to="/training/admin"
        className="inline-flex items-center text-forest-700 text-sm font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        Back to trainings
      </Link>

      <div>
        <ModuleTitleTRAINING />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Edit Training</h1>
      </div>

      <section className="patria-card p-6" data-testid="training-edit-meta">
        <h3 className="font-serif text-forest-900 text-xl">Details</h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="patria-label">Title *</label>
            <input
              className="patria-input mt-2 w-full"
              value={meta.title || ""}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              data-testid="training-edit-title"
            />
          </div>
          <div>
            <label className="patria-label">Category</label>
            <input
              className="patria-input mt-2 w-full"
              value={meta.category || ""}
              onChange={(e) => setMeta({ ...meta, category: e.target.value })}
            />
          </div>
          <div>
            <label className="patria-label">Duration (min)</label>
            <input
              type="number"
              min={0}
              className="patria-input mt-2 w-full"
              value={meta.duration_minutes || 0}
              onChange={(e) => setMeta({ ...meta, duration_minutes: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="patria-label">Default due date</label>
            <input
              type="date"
              className="patria-input mt-2 w-full"
              value={(meta.due_date || "").split("T")[0]}
              onChange={(e) => setMeta({ ...meta, due_date: e.target.value })}
            />
          </div>
          <div>
            <label className="patria-label">Video source</label>
            <select
              className="patria-input mt-2 w-full"
              value={meta.video_source}
              onChange={(e) => setMeta({ ...meta, video_source: e.target.value })}
              data-testid="training-edit-video-source"
            >
              <option value="embed">Embedded YouTube/Vimeo</option>
              <option value="upload">Direct upload</option>
            </select>
          </div>
          {meta.video_source === "embed" ? (
            <div>
              <label className="patria-label">Video URL</label>
              <input
                className="patria-input mt-2 w-full font-mono text-xs"
                value={meta.video_embed_url || ""}
                onChange={(e) => setMeta({ ...meta, video_embed_url: e.target.value })}
              />
            </div>
          ) : (
            <div>
              <label className="patria-label">Upload video (max 100 MB)</label>
              <label
                className="patria-btn-secondary mt-2 inline-flex items-center cursor-pointer w-full justify-center"
                data-testid="training-edit-video-upload"
              >
                <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {training.video_storage_key ? "Replace uploaded video" : "Upload video"}
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime"
                  onChange={uploadVideo}
                  className="hidden"
                />
              </label>
              {training.video_storage_key && (
                <p className="mt-1 text-xs text-charcoal-muted">
                  Current: {Math.round((training.video_size_bytes || 0) / 1024)} KB ({training.video_mime})
                </p>
              )}
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="patria-label">Description</label>
            <textarea
              className="patria-input mt-2 w-full"
              rows={3}
              value={meta.description || ""}
              onChange={(e) => setMeta({ ...meta, description: e.target.value })}
            />
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!meta.required}
              onChange={(e) => setMeta({ ...meta, required: e.target.checked })}
            />
            Mark as required
          </label>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm" data-testid="training-edit-visible-to-volunteers-row">
            <input
              type="checkbox"
              checked={!!meta.visible_to_volunteers}
              onChange={(e) => setMeta({ ...meta, visible_to_volunteers: e.target.checked })}
              data-testid="training-edit-visible-to-volunteers"
            />
            Visible to volunteers (required before assigning to volunteer audiences)
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={saveMeta}
            disabled={busy}
            className="patria-btn-primary"
            data-testid="training-edit-save-meta"
          >
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Save details
          </button>
        </div>
      </section>

      <section className="patria-card p-6" data-testid="training-edit-quiz">
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif text-forest-900 text-xl">Quiz</h3>
          <label className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={quiz.enabled}
              onChange={(e) => setQuiz({ ...quiz, enabled: e.target.checked })}
              data-testid="training-quiz-enabled"
            />
            Enabled
          </label>
        </div>
        <div className="mt-3">
          <label className="patria-label">Passing score (%)</label>
          <input
            type="number"
            min={1}
            max={100}
            className="patria-input mt-2 w-32"
            value={quiz.passing_score_pct}
            onChange={(e) => setQuiz({ ...quiz, passing_score_pct: Number(e.target.value) })}
          />
        </div>
        <div className="mt-6 space-y-4">
          {quiz.questions.map((q, i) => (
            <div key={q.id || i} className="patria-card p-4" data-testid={`training-edit-q-${i}`}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-xs uppercase tracking-widest text-charcoal-muted">Q{i + 1}</p>
                <button
                  onClick={() => removeQuestion(i)}
                  className="text-terracotta-600 hover:text-terracotta-500 text-xs"
                >
                  <Trash2 className="h-3.5 w-3.5 inline mr-1" />
                  Remove
                </button>
              </div>
              <textarea
                className="patria-input mt-2 w-full"
                rows={2}
                value={q.text}
                onChange={(e) => updateQuestion(i, { text: e.target.value })}
                placeholder="Question text"
              />
              <div className="mt-3 space-y-2">
                {q.choices.map((c, ci) => (
                  <div key={ci} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`q-${i}-correct`}
                      checked={!!c.correct}
                      onChange={() => updateChoice(i, ci, { correct: true })}
                      title="Correct answer"
                    />
                    <input
                      className="patria-input py-1.5 text-sm w-full"
                      value={c.text}
                      onChange={(e) => updateChoice(i, ci, { text: e.target.value })}
                      placeholder="Choice text"
                    />
                    {q.choices.length > 2 && (
                      <button
                        onClick={() => removeChoice(i, ci)}
                        className="text-terracotta-600 text-xs"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => addChoice(i)}
                className="patria-link text-xs mt-2"
              >
                + Add choice
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-between">
          <button
            onClick={addQuestion}
            className="patria-btn-secondary"
            data-testid="training-quiz-add-question"
          >
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Add question
          </button>
          <button
            onClick={saveQuiz}
            disabled={busy}
            className="patria-btn-primary"
            data-testid="training-quiz-save"
          >
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Save quiz
          </button>
        </div>
      </section>
    </div>
  );
}
